import {
	Box,
	Container,
	Grid,
	List,
	ListItem,
	ListItemText,
	styled,
	Typography,
} from '@mui/material';
import Logo from '../assets/images/Logo.png';
import { Link } from 'react-router-dom';

function TermsOfUse() {
	return (
		<Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
			<Grid
				container
				flexDirection={'row'}
				alignItems={'center'}
				justifyContent={'center'}>
				<Box
					component={'img'}
					src={Logo}
					sx={{
						padding: '2px',
						backgroundColor: '#FFFFFF',
						borderRadius: '5px',
					}}
					width={300}
					height={80}
				/>
			</Grid>
			<Box sx={{ mb: 4, mt: 4 }}>
				<Typography fontSize={'28px'} component="h1" gutterBottom>
					Senzcare.AI Terms of Use
				</Typography>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					1. Terms of Use
				</Typography>
				<StyledTypography>
					Welcome to SenzCare.AI. These Terms of Use (“Terms”) govern
					your access to and use of our websites, mobile applications,
					and related services (collectively, the “Platform”). By
					creating an account, accessing, or using SenzCare.AI, you
					agree to these Terms. Please read them carefully.
				</StyledTypography>
				<StyledTypography sx={{ marginTop: '10px' }}>
					If you do not agree to these Terms, you may not use the
					Platform.
				</StyledTypography>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					2. When These Terms Apply
				</Typography>
				<StyledTypography>
					These Terms apply when you interact with SenzCare.AI as an
					individual user, such as when you create an account, access
					AI-powered care features, use our accessibility-enabled
					mobile applications or browse our website.
				</StyledTypography>
				<StyledTypography sx={{ marginTop: '10px' }}>
					If you use SenzCare.AI under the management of a company,
					care organization, or other legal entity, then your access
					is also subject to that entity&apos;s agreement with us. In
					such cases, we provide the services on behalf of and in
					accordance with our contract with that entity.
				</StyledTypography>
				<StyledTypography sx={{ marginTop: '10px' }}>
					We may update these Terms from time to time. If changes are
					material, we will provide notice (e.g: via email, in-app
					notification, or posting on our website). Continued use of
					the Platform after such updates constitutes acceptance.
				</StyledTypography>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					3. Eligibility
				</Typography>
				<StyledTypography>
					To use SenzCare.AI, you must
				</StyledTypography>
				<List sx={{ pl: 1 }}>
					<StyledListItem>
						<ListItemText primary="• Be at least 18 years old (or the age of majority in your jurisdiction)." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Have the legal capacity to enter into these Terms." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Not be prohibited from using the Platform under applicable laws." />
					</StyledListItem>
				</List>
				<StyledTypography sx={{ marginTop: '10px' }}>
					For organizations using the Platform, administrators are
					responsible for ensuring compliance with these Terms by
					their staff.
				</StyledTypography>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					4. Your Account and Responsibilities
				</Typography>
				<List sx={{ pl: 1 }}>
					<StyledListItem>
						<ListItemText primary="• You must provide accurate and complete information when creating your account." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• You are responsible for maintaining the confidentiality of your login credentials" />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• You agree not to share your account with others or use another user's account without authorization." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• You agree to notify us immediately if you suspect unauthorized use of your account." />
					</StyledListItem>
				</List>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					5. Acceptable Use
				</Typography>
				<StyledTypography>
					You agree not to misuse the Platform, including by:
				</StyledTypography>
				<List sx={{ pl: 1 }}>
					<StyledListItem>
						<ListItemText primary="• Uploading false, misleading or unlawful content." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Attempting to access systems or data you are not authorized to access." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Interfering with or disrupting Platform functionality." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Circumventing security features or usage limitations." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Using the Platform to harass, discriminate, or harm others." />
					</StyledListItem>
				</List>
				<StyledTypography sx={{ marginTop: '10px' }}>
					We may suspend or terminate accounts that violate these
					Terms.
				</StyledTypography>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					6. Accessibility Features (Mobile App Only)
				</Typography>
				<StyledTypography>
					The SenzCare.AI mobile application requires access to
					certain accessibility features in order to provide real-time
					AI enhancements of care notes. Specifically:
				</StyledTypography>
				<List sx={{ pl: 1 }}>
					<StyledListItem>
						<ListItemText primary="• Care Note Input Capture - The app detects and captures text entered in care note fields to generate AI suggestions and enhancements." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Accessibility Permission - The app uses the Android Accessibility Service strictly to detect and enhance text entered into care note fields." />
					</StyledListItem>
				</List>
				<StyledTypography sx={{ marginTop: '10px' }}>
					We do not access or process information outside of these
					fields (e.g., passwords, payment data, or unrelated app
					content). Users can enable or disable this permission at any
					time through their device settings, but the app’s core
					functionality may be limited without it.
				</StyledTypography>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					7. Content and Data
				</Typography>
				<List sx={{ pl: 1 }}>
					<StyledListItem>
						<ListItemText primary="• You retain ownership of the content and data you upload into the Platform." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• By using SenzCare.AI, you grant us a limited license to process and store your data solely for providing and improving our services." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• We do not sell your personal data." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText
							primary={
								<>
									• For more details, see our{' '}
									<Link
										to="https://senzcare.senzmatica.com/privacy-policy"
										underline="hover"
										target="_blank">
										Privacy Policy
									</Link>
									.
								</>
							}
						/>
					</StyledListItem>
				</List>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					8. Paid Services
				</Typography>
				<StyledTypography>
					Some features may require payment in future.
				</StyledTypography>
				<List sx={{ pl: 1 }}>
					<StyledListItem>
						<ListItemText primary="• Fees will be stated at the time of purchase." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Payments will be processed securely through third-party providers." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Subscriptions may auto-renew unless canceled before the renewal date." />
					</StyledListItem>
				</List>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					9. Intellectual Property
				</Typography>
				<List sx={{ pl: 1 }}>
					<StyledListItem>
						<ListItemText primary="• The Platform, including software, logos, design, and content (excluding user-uploaded data), is owned by SenzCare.AI and its licensors." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• You may not copy, modify, distribute, or reverse-engineer any part of the Platform except as permitted by law." />
					</StyledListItem>
				</List>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					10. Disclaimer of Warranties
				</Typography>
				<StyledTypography>
					The Platform is provided “as is” and “as available.”
				</StyledTypography>
				<List sx={{ pl: 1 }}>
					<StyledListItem>
						<ListItemText primary="• We do not guarantee uninterrupted or error-free operation." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• We do not provide medical or legal advice. Any insights or recommendations from the Platform are for informational purposes only and should not replace professional judgment." />
					</StyledListItem>
				</List>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					11. Limitation of Liability
				</Typography>
				<StyledTypography>
					To the extent permitted by law, SenzCare.AI and its
					affiliates are not liable for:
				</StyledTypography>
				<List sx={{ pl: 1 }}>
					<StyledListItem>
						<ListItemText primary="• Indirect, incidental, or consequential damages." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Loss of data, profits, or business opportunities." />
					</StyledListItem>
					<StyledListItem>
						<ListItemText primary="• Issues caused by unauthorized access, third-party services, or your misuse of the Platform." />
					</StyledListItem>
				</List>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h6" component="h2" gutterBottom>
					12. Termination
				</Typography>
				<StyledTypography>
					We may suspend or terminate your account if you violate
					these Terms. You may stop using the Platform at any time by
					deleting your account.
				</StyledTypography>
			</Box>
			<Box>
				<Typography variant="h6" component="h2" gutterBottom>
					13. Contact
				</Typography>
				<StyledTypography
					variant="body1"
					color="text.secondary"
					gutterBottom>
					If you have any general questions, comments or complaints
					regarding the SenzCare.AI platform, please send an e-mail
				</StyledTypography>
				<StyledTypography variant="body1">
					Mail :{' '}
					<Link to="mailto:info@senzmate.com" underline="hover">
						info@senzmate.com
					</Link>
				</StyledTypography>
			</Box>
		</Container>
	);
}

const StyledListItem = styled(ListItem)(({ theme }) => ({
	paddingTop: '0px',
	paddingBottom: '0px',
	color: '#646464',
	fontSize: '14px',
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
	color: '#324054',
}));

export default TermsOfUse;
